import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { FaPlus, FaLock, FaEdit } from "react-icons/fa";
import { FiCheck, FiX, FiMail } from "react-icons/fi";
import { useComponentTranslation } from "@/services/i18n";
import api from "@/services/api";
import useStore from "@/services/store";
import toast from "react-hot-toast";
import Modal from "@/components/modal";

const translations = {
  en: {
    title: "Pipeline",
    subtitle: "Define the stages candidates will go through in your hiring process",
    system_stage: "System",
    edit_stage: "Edit stage",
    add_stage: "Add a stage",
    new_stage_title: "Create New Stage",
    new_stage_name: "Stage Name",
    create: "Create",
    cancel: "Cancel",
    error_creating: "Error creating stage",
  },
  fr: {
    title: "Pipeline",
    subtitle: "Définissez les étapes par lesquelles les candidats passeront dans votre processus de recrutement",
    system_stage: "Système",
    edit_stage: "Modifier l'étape",
    add_stage: "Ajouter une étape",
    new_stage_title: "Créer une nouvelle étape",
    new_stage_name: "Nom de l'étape",
    create: "Créer",
    cancel: "Annuler",
    error_creating: "Erreur lors de la création de l'étape",
  },
};

// Default stages structure
// const DEFAULT_STAGES = [
//   { id: "applied", name: "Application", type: "system", locked: true, color: "bg-blue-500" },
//   { id: "preselection", name: "Preselection", type: "screening", locked: false, color: "bg-blue-500" },
//   { id: "interview", name: "Interview", type: "interview", locked: false, color: "bg-orange-400" },
//   { id: "selected", name: "Selected", type: "offer", locked: false, color: "bg-green-500" },
//   { id: "refused", name: "Refused", type: "refusal", locked: true, color: "bg-red-500" },
// ];

function CreateStageButton({ job, position, onStageCreated, variant = "full" }) {
  const { t } = useComponentTranslation(translations);
  const navigate = useNavigate();
  const { organization } = useStore();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newStageName, setNewStageName] = useState("");
  const [creating, setCreating] = useState(false);

  const handleCreateStage = async (e) => {
    e.preventDefault();
    if (!newStageName.trim()) return;

    try {
      setCreating(true);

      const response = await api.post("/stage", {
        job_id: job._id || job.id,
        name: newStageName,
        position: position,
        key: `STAGE_${new Date().getTime()}`,
        organization_id: organization?._id,
        organization_name: organization?.name,
      });

      if (response.ok) {
        setIsAddModalOpen(false);
        setNewStageName("");

        // Notify parent to refresh list
        if (onStageCreated) {
          onStageCreated();
        }

        // Redirect to edit the newly created stage
        navigate(response.data._id);
      } else {
        toast.error(t("error_creating"));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("error_creating"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      {variant === "compact" ? (
        <button onClick={() => setIsAddModalOpen(true)} className="btn btn-primary shrink-0">
          <FaPlus className="w-3.5 h-3.5" />
          {t("add_stage")}
        </button>
      ) : (
        <div className="flex items-start gap-4 mt-2 group relative">
          <div className="flex-1">
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-primary hover:text-primary hover:bg-primary/5 transition-all flex items-center justify-center gap-2 font-medium"
            >
              <FaPlus className="w-4 h-4" />
              {t("add_stage")}
            </button>
          </div>
        </div>
      )}

      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t("new_stage_title")}</h2>
          <form onSubmit={handleCreateStage}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t("new_stage_name")}</label>
              <input
                type="text"
                value={newStageName}
                onChange={(e) => setNewStageName(e.target.value)}
                placeholder="e.g. Technical Test"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium">
                {t("cancel")}
              </button>
              <button
                type="submit"
                disabled={creating || !newStageName.trim()}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {creating && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {t("create")}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}

export default function HiringFlow({ job, setJob }) {
  const { t } = useComponentTranslation(translations);
  const { organization } = useStore();
  const { id } = useParams();
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);

  useEffect(() => {
    fetchDefaultStages();
  }, [id]);

  const fetchDefaultStages = async () => {
    try {
      const response = await api.post("/stage/search", { job_id: id });
      if (response.ok && response.data) {
        setStages(response.data);
      }
    } catch (e) {
      console.error("Error fetching stages:", e);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(stages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setStages(items);
    saveStagesOrder(items);
  };

  const saveStagesOrder = async (newStages) => {
    try {
      const updates = newStages.map((stage, index) => {
        const nextStage = newStages[index + 1];
        return {
          id: stage._id || stage.id,
          position: index,
          next_stage: nextStage ? nextStage._id || nextStage.id : null,
        };
      });

      const realUpdates = updates.filter((u) => u.id && u.id.length > 10);

      if (realUpdates.length > 0) {
        await api.put(`/stage/reorder`, { stages: realUpdates });
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to save order");
    }
  };

  const handleEditStage = (stage) => {
    const stageId = stage._id || stage.id;
    navigate(stageId);
  };

  const isSystemStage = (stage) => {
    return ["APPLIED", "SELECTED", "REFUSED"].includes(stage?.key);
  };

  const customStages = stages.filter((s) => !isSystemStage(s));
  const hasScreeningStages = customStages.length > 0;

  const renderStageCard = (stage, index, provided = null) => {
    if (!stage) return null;

    const isSelected = stage.key === "SELECTED";
    const isRefused = stage.key === "REFUSED";
    const isSystem = isSystemStage(stage);

    const getBadgeStyle = () => {
      if (isSelected) return "bg-emerald-100 text-emerald-600 border border-emerald-200";
      if (isRefused) return "bg-red-100 text-red-600 border border-red-200";
      return "bg-indigo-100 text-indigo-600 border border-indigo-200";
    };

    const getBadgeContent = () => {
      if (isSelected) return <FiCheck className="w-4 h-4" />;
      if (isRefused) return <FiX className="w-4 h-4" />;
      return index + 1;
    };

    return (
      <div
        id={`stage-${stage._id || stage.id}`}
        ref={provided?.innerRef}
        {...provided?.draggableProps}
        {...provided?.dragHandleProps}
        className={`relative mb-3 ${provided ? "" : "opacity-100"}`}
      >
        <div className="bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors group">
          {/* Header */}
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3 overflow-hidden">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm shrink-0 ${getBadgeStyle()}`}>{getBadgeContent()}</div>

              <div className="min-w-0">
                <span className="font-bold text-gray-900 text-base truncate block">{stage.name}</span>
                {stage.description && <span className="text-xs text-gray-400 truncate block">{stage.description}</span>}
              </div>

              {isSystem && (
                <div className="px-2 py-0.5 rounded text-[10px] uppercase font-bold bg-gray-100 text-gray-500 border border-gray-200 flex items-center gap-1">
                  <FaLock className="w-2 h-2" />
                  {t("system_stage")}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {stage.user_id && (
                <div className="flex items-center gap-1">
                  {stage.notify && (
                    <div title="Notifications enabled" className="bg-yellow-100 p-1 rounded-full">
                      <FiMail className="w-3 h-3 text-yellow-600" />
                    </div>
                  )}
                  <div title={`Assigned to ${stage.user_name || "User"}`}>
                    {stage.user_avatar ? (
                      <img src={stage.user_avatar} alt="" className="w-6 h-6 rounded-full border border-gray-200" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold border border-indigo-200">
                        {(stage.user_name?.[0] || "U").toUpperCase()}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <button onClick={() => handleEditStage(stage)} className="p-2 text-gray-400 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors" title={t("edit_stage")}>
                <FaEdit className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const appliedStage = stages.find((s) => s.key === "APPLIED");
  const selectedStage = stages.find((s) => s.key === "SELECTED");
  const refusedStage = stages.find((s) => s.key === "REFUSED");
  const middleStages = stages.filter((s) => !isSystemStage(s));

  return (
    <div className="py-6 max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{t("title")}</h2>
          <p className="text-sm text-gray-500 mt-1">{t("subtitle")}</p>
        </div>
        <CreateStageButton job={job} position={stages.length} onStageCreated={fetchDefaultStages} variant="compact" />
      </div>

      <div>
        {appliedStage && renderStageCard(appliedStage, 0)}

        {hasScreeningStages && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="pipeline-stages">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {middleStages.map((stage, index) => (
                    <Draggable key={stage._id || stage.id} draggableId={stage._id || stage.id} index={index} isDragDisabled={false}>
                      {(provided) => renderStageCard(stage, index + 1, provided)}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {selectedStage && renderStageCard(selectedStage, stages.indexOf(selectedStage))}
        {refusedStage && renderStageCard(refusedStage, stages.indexOf(refusedStage))}
      </div>
    </div>
  );
}
